import type React from "react";
import { memo } from "react";
import { PostThread } from "@/features/study-group-stream/components/PostThread";
import {
  buildSearchResultPostCard,
  buildSearchResultThreadCommentSectionProps,
  buildSearchResultEventLinkPreviewProps,
  getSearchResultPostObjectId,
} from "@/features/study-group-stream/utils/streamSearchResultItem";
import { SEARCH_RESULT_LIST_WRAPPER_CLASS } from "@/features/study-group-stream/components/StreamSearchResultStateViews";
import { EMPTY_SEARCH_MATCH_STATE } from "@/features/study-group-stream/utils/streamSearchMatchUtils";
import type {
  SearchResultItemProps,
  SearchResultItemListProps,
} from "@/features/study-group-stream/utils/streamSearchResultTypes";

type SearchResultItemInternalProps = SearchResultItemProps & {
  isFirstResultFamily: boolean;
};

const SearchResultItemRaw: React.FC<SearchResultItemInternalProps> = ({
  post,
  searchMatchState,
  groupContext,
  unknownAuthorText,
  onPostReaction,
  isReactingPost = false,
  isFirstResultFamily,
}) => {
  const { matchedCommentIds, isCurrentPostMatched } = searchMatchState;
  const postObjectId = getSearchResultPostObjectId(post);

  const postCard = buildSearchResultPostCard({
    post,
    groupContext,
    unknownAuthorText,
    isCurrentPostHighlighted: isCurrentPostMatched,
    isReactingPost,
    onReaction: onPostReaction
      ? async (reaction) => onPostReaction(postObjectId, reaction)
      : undefined,
  });

  return (
    <PostThread
      postCard={postCard}
      commentSectionProps={buildSearchResultThreadCommentSectionProps({
        post,
        matchedCommentIds,
        isCurrentPostMatched,
        groupContext,
        postObjectId,
        isFirstResultFamily,
      })}
      eventLinkPreviewProps={buildSearchResultEventLinkPreviewProps({
        groupContext,
      })}
    />
  );
};

const SearchResultItem: React.FC<SearchResultItemInternalProps> =
  memo(SearchResultItemRaw);

SearchResultItem.displayName = "SearchResultItem";

export const SearchResultItemList: React.FC<SearchResultItemListProps> = memo(
  ({
    streamContents,
    searchMatchStates,
    groupContext,
    unknownAuthorText,
    onPostReaction,
    reactingPosts,
  }) => (
    <div className={SEARCH_RESULT_LIST_WRAPPER_CLASS}>
      {streamContents.map((post, index) => (
        <SearchResultItem
          key={post.id}
          post={post}
          searchMatchState={
            searchMatchStates.get(post.id) ?? EMPTY_SEARCH_MATCH_STATE
          }
          groupContext={groupContext}
          unknownAuthorText={unknownAuthorText}
          onPostReaction={onPostReaction}
          isReactingPost={reactingPosts?.has(getSearchResultPostObjectId(post))}
          isFirstResultFamily={index === 0}
        />
      ))}
    </div>
  ),
);

SearchResultItemList.displayName = "SearchResultItemList";
